import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Menu, 
  MenuItem, 
  Button,
  Tooltip,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { FaRegFileAlt } from 'react-icons/fa';
import { IoMdArrowDropdown } from 'react-icons/io';
import { MdOutlinePhoneAndroid } from 'react-icons/md';
import { BiMobileAlt } from 'react-icons/bi';

const FormDropdown = ({ onFormSelect, formStates, isProcessing = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFormSelect = (formType) => {
    onFormSelect(formType);
    handleClose();
  };

  const isDisabled = formStates.prepaidDisabled && formStates.postpaidDisabled;
  const isAnyFormOpen = formStates.isOpen;

  // Determine button state based on disabled forms
  const getButtonTooltip = () => {
    if (isProcessing) return "Processing message, please wait";
    if (isAnyFormOpen) return "Close current form before opening another";
    if (isDisabled) return "Forms are currently disabled";
    return "Click to select a form";
  };
  
  return (
    <Box>
      <Tooltip title={getButtonTooltip()}>
        <span>
          <Button
            id="form-dropdown-button"
            aria-controls={open ? 'form-dropdown-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            disabled={isDisabled || isAnyFormOpen || isProcessing}
            startIcon={<FaRegFileAlt />}
            endIcon={<IoMdArrowDropdown />}
            sx={{
              border: '1px solid',
              borderColor: isDisabled || isAnyFormOpen || isProcessing ? 'grey.400' : 'primary.main',
              borderRadius: '20px',
              p: '6px 16px',
              color: isDisabled || isAnyFormOpen || isProcessing ? 'grey.500' : 'primary.main',
              fontWeight: 500,
              transition: 'all 0.2s ease-in-out',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDisabled || isAnyFormOpen || isProcessing ? 'inherit' : 'rgba(0, 0, 0, 0.04)',
                transform: isDisabled || isAnyFormOpen || isProcessing ? 'none' : 'translateY(-1px)',
                boxShadow: isDisabled || isAnyFormOpen || isProcessing ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
              }
            }}
          >
            Open Form
          </Button>
        </span>
      </Tooltip>
      
      <Menu
        id="form-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'form-dropdown-button',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            borderRadius: '8px'
          }
        }}
      >
        <MenuItem 
          onClick={() => handleFormSelect('prepaid')} 
          disabled={formStates.prepaidDisabled}
          sx={{ 
            py: 1.5, 
            minWidth: '200px',
            '&:hover': {
              backgroundColor: formStates.prepaidDisabled ? 'inherit' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <ListItemIcon>
            <MdOutlinePhoneAndroid color={formStates.prepaidDisabled ? "#bdbdbd" : "#1976d2"} />
          </ListItemIcon>
          <ListItemText>
            <Typography sx={{ fontWeight: formStates.prepaidDisabled ? 400 : 500 }}>
              Prepaid Form
            </Typography>
          </ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={() => handleFormSelect('postpaid')} 
          disabled={formStates.postpaidDisabled}
          sx={{ 
            py: 1.5,
            '&:hover': {
              backgroundColor: formStates.postpaidDisabled ? 'inherit' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <ListItemIcon>
            <BiMobileAlt color={formStates.postpaidDisabled ? "#bdbdbd" : "#1976d2"} />
          </ListItemIcon>
          <ListItemText>
            <Typography sx={{ fontWeight: formStates.postpaidDisabled ? 400 : 500 }}>
              Postpaid Form
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

FormDropdown.propTypes = {
  onFormSelect: PropTypes.func.isRequired,
  formStates: PropTypes.shape({
    isOpen: PropTypes.bool.isRequired,
    prepaidDisabled: PropTypes.bool.isRequired,
    postpaidDisabled: PropTypes.bool.isRequired
  }).isRequired,
  isProcessing: PropTypes.bool
};

export default FormDropdown;